import {
  environment,
  featureFlags,
  getEnvironmentConfig,
  isFeatureEnabled,
} from '../config/featureFlags';

/**
 * Custom hook for accessing feature flags and environment configuration
 * @returns {Object} Feature flags, environment info, and helper functions
 */
export const useFeatureFlags = () => {
  return {
    // Feature flags
    ...featureFlags,

    // Environment information
    environment,

    // Helper functions
    isFeatureEnabled,
    getEnvironmentConfig,

    // Convenience flags
    showPreviewBanner: featureFlags.previewBannerEnabled,
    canSubmitForms: featureFlags.emailFormsEnabled,
    shouldTrackAnalytics: featureFlags.googleAnalyticsEnabled,
    isDebugMode: featureFlags.debugMode,
  };
};

/**
 * Higher-order component to conditionally render based on feature flags
 * @param {string} featureName - The feature flag name
 * @param {React.Component} fallback - Component to render if feature is disabled
 * @returns {Function} HOC function
 */
export const withFeatureFlag =
  (featureName, fallback = null) =>
  (Component) => {
    return function FeatureGatedComponent(props) {
      if (!isFeatureEnabled(featureName)) {
        return fallback;
      }
      return <Component {...props} />;
    };
  };

/**
 * Component to conditionally render children based on feature flag
 * @param {Object} props - Component props
 * @param {string} props.feature - Feature flag name
 * @param {React.ReactNode} props.children - Children to render if feature is enabled
 * @param {React.ReactNode} props.fallback - Fallback to render if feature is disabled
 * @returns {React.ReactNode} Rendered component based on feature flag state
 */
export const FeatureFlag = ({ feature, children, fallback = null }) => {
  return isFeatureEnabled(feature) ? children : fallback;
};
